/**
 * NovaContaPagarPage Component
 * 
 * Página de criação de nova conta a pagar com suporte a:
 * - Conta única
 * - Parcelamento
 * - Recorrência (valor fixo e variável)
 * 
 * Refatorado para seguir o design moderno do documento de aprimoramento.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  ArrowLeft, Save, Search, Loader2,
  Briefcase, Layers, HelpCircle,
  Wallet, Building2, User, Receipt,
  ListOrdered, Repeat, TrendingUp,
  Check, FileText,
  ChevronDown, Target, ListTree, Info
} from 'lucide-react'
import SelectPersonModal from '../../../../components/ERP/Modal/SelectPersonModal'
import { 
  accountPayableService, 
  financialCategoryService,
  costCenterService,
  financialAccountService,
  cashStatementService
} from '../../../../services/erpApi'
import { toDateInputValue } from '../../../../utils/dateUtils'
import {
  consumeCashStatementPayablePrefill,
  notifyCashStatementPayableCreated
} from '../../../../utils/cashStatementPayablePrefill'
import './NovaContaPagarPage.css'

// Componente de Dropdown Customizado
function CustomDropdown({ 
  label, 
  options, 
  value, 
  onChange, 
  icon: Icon,
  placeholder = "Selecione...",
  error
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    
    const updateMenuPosition = () => {
      if (menuRef.current && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect()
        const menu = menuRef.current
        const viewportHeight = window.innerHeight
        const menuHeight = 240 // altura aproximada do menu
        const spaceBelow = viewportHeight - buttonRect.bottom
        
        // Usar position: fixed para garantir que apareça acima de tudo
        menu.style.position = 'fixed'
        menu.style.width = `${buttonRect.width}px`
        menu.style.left = `${buttonRect.left}px`
        
        // Se não há espaço suficiente abaixo, mostrar acima
        if (spaceBelow < menuHeight && buttonRect.top > menuHeight) {
          menu.style.top = 'auto'
          menu.style.bottom = `${viewportHeight - buttonRect.top + 8}px`
        } else {
          // Posicionar logo abaixo do botão
          menu.style.top = `${buttonRect.bottom + 8}px`
          menu.style.bottom = 'auto'
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(updateMenuPosition, 0)
      
      // Ajustar posição ao fazer scroll ou redimensionar
      window.addEventListener('scroll', updateMenuPosition, true)
      window.addEventListener('resize', updateMenuPosition)
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', updateMenuPosition, true)
        window.removeEventListener('resize', updateMenuPosition)
      }
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const displayValue = options.find(o => o.value === value)?.label || value || placeholder

  return (
    <div className={`custom-dropdown-wrapper ${isOpen ? 'dropdown-open' : ''}`} ref={dropdownRef}>
      <label className="custom-dropdown-label">{label}</label>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`custom-dropdown-button ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}
      >
        <div className="custom-dropdown-button-content">
          <Icon size={16} className={`custom-dropdown-icon ${isOpen ? 'active' : ''}`} />
          <span className={`custom-dropdown-text ${value ? 'has-value' : ''}`}>
            {displayValue}
          </span>
        </div>
        <ChevronDown size={14} className={`custom-dropdown-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu" ref={menuRef}>
          <div className="custom-dropdown-menu-content">
            {options.map((opt) => {
              const isActive = value === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value)
                    setIsOpen(false)
                  }}
                  className={`custom-dropdown-option ${isActive ? 'active' : ''}`}
                >
                  <span className="custom-dropdown-option-label">{opt.label}</span>
                  {isActive && <Check size={14} className="custom-dropdown-option-check" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  )
}

function NovaContaPagarPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const prefillAppliedRef = useRef(false)
  const cashStatementContextRef = useRef(null)
  
  // Estados principais
  const [entityType, setEntityType] = useState('supplier') // supplier, customer, user
  const [formData, setFormData] = useState({
    personType: '', // supplier, customer, user
    personId: '',
    personName: '',
    description: '',
    amount: '',
    invoiceNumber: '',
    categoryId: '',
    subcategoryId: '',
    costCenterId: '',
    financialAccountId: '',
    installmentType: 'single', // single, installment, recurring_fixed, recurring_variable
    numberOfInstallments: 1,
    firstDueDate: '',
    competenceDate: '',
    period: 'monthly', // monthly, biweekly
    competenceDateType: 'due', // due, previous_due
    markAsPaid: false,
    observations: ''
  })

  // Estados auxiliares
  const [showPersonModal, setShowPersonModal] = useState(false)
  const [categorias, setCategorias] = useState([])
  const [centrosCusto, setCentrosCusto] = useState([])
  const [contasFinanceiras, setContasFinanceiras] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(true)

  useEffect(() => {
    loadOptions()
  }, [])

  useEffect(() => {
    if (loadingOptions || prefillAppliedRef.current) return

    const context = location.state?.prefill
      ? location.state
      : consumeCashStatementPayablePrefill()

    if (!context?.prefill) return

    prefillAppliedRef.current = true
    cashStatementContextRef.current = context

    const prefill = context.prefill
    setFormData(prev => ({
      ...prev,
      description: prefill.description ?? prev.description,
      amount: prefill.amount ?? prev.amount,
      firstDueDate: prefill.firstDueDate ? toDateInputValue(prefill.firstDueDate) : prev.firstDueDate,
      competenceDate: prefill.competenceDate ? toDateInputValue(prefill.competenceDate) : prev.competenceDate,
      financialAccountId: prefill.financialAccountId ?? prev.financialAccountId,
      observations: prefill.observations ?? prev.observations,
      installmentType: 'single',
      numberOfInstallments: 1
    }))
  }, [loadingOptions, location.state])

  const loadOptions = async () => {
    setLoadingOptions(true)
    try {
      const [categoriasRes, centrosRes, contasRes] = await Promise.all([
        financialCategoryService.getAll({ type: 'expense', active: true }),
        costCenterService.getAll({ active: true }),
        financialAccountService.getAll({ active: true })
      ])

      if (categoriasRes.success) setCategorias(categoriasRes.data || [])
      if (centrosRes.success) setCentrosCusto(centrosRes.data || [])
      if (contasRes.success) setContasFinanceiras(contasRes.data || [])
    } catch (error) {
      console.error('Erro ao carregar opções:', error)
    } finally {
      setLoadingOptions(false)
    }
  }

  const handleSelectPerson = (person) => {
    setFormData({
      ...formData,
      personType: person.type,
      personId: person.id,
      personName: person.name
    })
    setEntityType(person.type)
  }

  const generateInstallments = useCallback(() => {
    const numParcelas = parseInt(formData.numberOfInstallments) || 1
    const valorTotal = parseFloat(formData.amount) || 0
    const valorParcela = valorTotal / numParcelas
    const primeiraData = new Date(formData.firstDueDate)
    
    const novasParcelas = []
    for (let i = 0; i < numParcelas; i++) {
      const dataVencimento = new Date(primeiraData)
      dataVencimento.setMonth(dataVencimento.getMonth() + i)
      
      novasParcelas.push({
        number: i + 1,
        dueDate: dataVencimento.toISOString().split('T')[0],
        amount: i === numParcelas - 1 
          ? valorTotal - (valorParcela * (numParcelas - 1))
          : parseFloat(valorParcela.toFixed(2))
      })
    }
    
    setParcelas(novasParcelas)
  }, [formData.amount, formData.firstDueDate, formData.numberOfInstallments])

  const generateRecurringInstallments = useCallback(() => {
    const valorTotal = parseFloat(formData.amount) || 0
    const primeiraData = new Date(formData.firstDueDate)
    const periodDays = formData.period === 'monthly' ? 30 : 15
    
    const novasParcelas = []
    for (let i = 0; i < 12; i++) {
      const dataVencimento = new Date(primeiraData)
      dataVencimento.setDate(dataVencimento.getDate() + (periodDays * i))
      
      novasParcelas.push({
        number: i + 1,
        dueDate: dataVencimento.toISOString().split('T')[0],
        amount: formData.installmentType === 'recurring_fixed' 
          ? valorTotal 
          : 0
      })
    }
    
    setParcelas(novasParcelas)
  }, [formData.amount, formData.firstDueDate, formData.installmentType, formData.period])

  useEffect(() => {
    if (formData.installmentType === 'installment' && formData.numberOfInstallments > 0 && formData.firstDueDate) {
      generateInstallments()
    } else if (formData.installmentType === 'single' && formData.firstDueDate) {
      setParcelas([{
        number: 1,
        dueDate: formData.firstDueDate,
        amount: parseFloat(formData.amount) || 0
      }])
    } else if ((formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && formData.firstDueDate) {
      generateRecurringInstallments()
    } else {
      setParcelas([])
    }
  }, [
    formData.amount,
    formData.firstDueDate,
    formData.installmentType,
    formData.numberOfInstallments,
    formData.period,
    generateInstallments,
    generateRecurringInstallments
  ])

  const updateParcelaAmount = (index, newAmount) => {
    const novasParcelas = [...parcelas]
    novasParcelas[index].amount = parseFloat(newAmount) || 0
    setParcelas(novasParcelas)
  }

  const updateParcelaDate = (index, newDate) => {
    const novasParcelas = [...parcelas]
    novasParcelas[index].dueDate = newDate
    setParcelas(novasParcelas)
  }

  const getObservationText = (parcela) => {
    let obs = formData.description
    if (formData.invoiceNumber) {
      obs += ` - NF: ${formData.invoiceNumber}`
    }
    if (formData.installmentType === 'installment') {
      obs += ` - Parcela ${parcela.number}/${parcelas.length}`
    } else if (formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') {
      obs += ` - ${parcela.number}º período`
    }
    if (formData.observations) {
      obs += ` | ${formData.observations}`
    }
    return obs
  }

  const handleSubmit = async () => {
    setErrors({})
    
    // Validações
    if (!formData.personId || !formData.personType) {
      setErrors({ personId: 'Selecione uma pessoa (Fornecedor, Cliente ou Usuário)' })
      return
    }

    if (!formData.description) {
      setErrors({ description: 'Descrição é obrigatória' })
      return
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setErrors({ amount: 'Valor deve ser maior que zero' })
      return
    }

    if (formData.installmentType === 'single' && !formData.firstDueDate) {
      setErrors({ firstDueDate: 'Data de vencimento é obrigatória' })
      return
    }

    if (formData.installmentType === 'installment' && parcelas.length === 0) {
      setErrors({ installments: 'Gere as parcelas antes de salvar' })
      return
    }

    if ((formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && parcelas.length === 0) {
      setErrors({ installments: 'Gere as parcelas antes de salvar' })
      return
    }

    setLoading(true)

    try {
      const contasParaCriar = parcelas.map(parcela => {
        const referenceData = {
          installmentType: formData.installmentType,
          installmentNumber: formData.installmentType === 'installment' ? parcela.number : null,
          totalInstallments: formData.installmentType === 'installment' ? parcelas.length : null,
          period: (formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') ? formData.period : null
        }

        let competenceDate = formData.competenceDate || null
        if (!competenceDate) {
          if (formData.competenceDateType === 'due') {
            competenceDate = parcela.dueDate
          } else if (formData.competenceDateType === 'previous_due') {
            const dueDate = new Date(parcela.dueDate)
            dueDate.setMonth(dueDate.getMonth() - 1)
            competenceDate = dueDate.toISOString().split('T')[0]
          }
        }

        const accountData = {
          amount: parcela.amount,
          dueDate: parcela.dueDate,
          competenceDate: competenceDate,
          status: formData.markAsPaid ? 'paid' : 'pending',
          categoryId: formData.subcategoryId || formData.categoryId || null,
          costCenterId: formData.costCenterId || null,
          financialAccountId: formData.financialAccountId || null,
          observation: getObservationText(parcela),
          referenceType: formData.installmentType !== 'single' ? 'installment' : null,
          referenceId: formData.installmentType !== 'single' ? JSON.stringify(referenceData) : null,
          paymentDate: formData.markAsPaid ? parcela.dueDate : null
        }

        if (formData.personType === 'supplier') {
          accountData.supplierId = formData.personId
        } else if (formData.personType === 'customer') {
          accountData.customerId = formData.personId
        } else if (formData.personType === 'user') {
          accountData.userId = formData.personId
        }

        return accountData
      })

      const promises = contasParaCriar.map(conta => accountPayableService.create(conta))
      const results = await Promise.all(promises)

      const hasError = results.some(r => !r.success)
      if (hasError) {
        const errorMessages = results
          .filter(r => !r.success)
          .map(r => r.message)
          .join(', ')
        setErrors({ submit: `Erro ao criar algumas contas: ${errorMessages}` })
        return
      }

      const cashStatementTransactionId =
        cashStatementContextRef.current?.prefill?.cashStatementTransactionId

      if (
        cashStatementTransactionId &&
        formData.installmentType === 'single' &&
        results.length === 1
      ) {
        const createdPayableId = results[0]?.data?.id
        if (createdPayableId) {
          const linkResponse = await cashStatementService.linkTransaction(
            cashStatementTransactionId,
            createdPayableId,
            'account_payable'
          )

          if (!linkResponse?.success) {
            setErrors({
              submit: `Conta criada, mas não foi possível conciliar no extrato: ${linkResponse?.message || 'erro desconhecido'}`
            })
            navigate('/erp/financeiro/contas-pagar')
            return
          }

          notifyCashStatementPayableCreated(cashStatementTransactionId)

          if (window.opener && !window.opener.closed) {
            window.close()
            setTimeout(() => {
              if (!window.closed) {
                navigate('/erp/financeiro/contas-pagar')
              }
            }, 400)
            return
          }
        }
      }

      navigate('/erp/financeiro/contas-pagar')
    } catch (error) {
      console.error('Erro ao criar contas:', error)
      setErrors({ submit: error.response?.data?.message || 'Erro ao criar contas a pagar' })
    } finally {
      setLoading(false)
    }
  }

  // Separar categorias principais (sem parent) e subcategorias
  const categoriasPrincipais = categorias.filter(c => !c.parent_id)
  const subcategorias = categorias.filter(c => c.parent_id)
  
  const categoryOptions = [
    { value: '', label: 'Selecione uma categoria' },
    ...categoriasPrincipais.map(c => ({ value: c.id, label: c.name }))
  ]

  // Subcategorias filtradas pela categoria principal selecionada
  const subcategoryOptions = [
    { value: '', label: 'Selecione uma subcategoria' },
    ...subcategorias
      .filter(sc => sc.parent_id === parseInt(formData.categoryId))
      .map(sc => ({ value: sc.id, label: sc.name }))
  ]

  const costCenterOptions = [
    { value: '', label: 'Selecione um centro de custo' },
    ...centrosCusto.map(c => ({ value: c.id, label: c.name }))
  ]

  const financialAccountOptions = [
    { value: '', label: 'Selecione uma conta bancária' },
    ...contasFinanceiras.map(c => ({ value: c.id, label: `${c.name} (${c.code})` }))
  ]

  const competencyRuleOptions = [
    { value: 'due', label: 'Mesma data de vencimento' },
    { value: 'previous_due', label: 'Mês anterior ao vencimento' }
  ]

  const periodOptions = [
    { value: 'monthly', label: 'Mensal' },
    { value: 'biweekly', label: 'Quinzenal' }
  ]

  return (
    <div className="new-account-payable-page">
      {/* Header */}
      <div className="new-account-payable-header">
        <div className="new-account-payable-header-left">
          <button 
            onClick={() => navigate('/erp/financeiro/contas-pagar')} 
            className="new-account-payable-back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="new-account-payable-title">
              Novo <span className="new-account-payable-title-accent">Lançamento</span>
            </h1>
            <p className="new-account-payable-subtitle">Configuração de Saída de Caixa</p>
          </div>
        </div>
        <div className="new-account-payable-header-actions">
          <button 
            type="button"
            onClick={() => navigate('/erp/financeiro/contas-pagar')}
            className="new-account-payable-cancel-button"
          >
            Cancelar
          </button>
          <button 
            onClick={handleSubmit}
            disabled={loading || !formData.personId || !formData.amount || parseFloat(formData.amount) <= 0}
            className="new-account-payable-save-button"
          >
            {loading ? <Loader2 className="new-account-payable-save-spinner" size={18} /> : <Save size={18} />}
            Efetivar Lançamento
          </button>
        </div>
      </div>

      {loadingOptions ? (
        <div className="new-account-payable-loading">
          <p>Carregando opções...</p>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="new-account-payable-form">
          {/* Coluna Principal */}
          <div className="new-account-payable-main-column">
            {/* Bloco 1: Identificação */}
            <div className="new-account-payable-card new-account-payable-card-identification">
              <h3 className="new-account-payable-card-title">
                <Briefcase className="new-account-payable-card-title-icon" size={16} />
                Identificação e Origem
              </h3>

              <div className="new-account-payable-card-content">
                {/* Seletor de Tipo de Entidade */}
                <div className="new-account-payable-entity-selector">
                  {[
                    { id: 'supplier', label: 'Fornecedor', icon: Building2 },
                    { id: 'customer', label: 'Cliente', icon: User },
                    { id: 'user', label: 'Funcionário', icon: Briefcase }
                  ].map(btn => (
                    <button
                      key={btn.id}
                      type="button"
                      onClick={() => {
                        setEntityType(btn.id)
                        setFormData({ ...formData, personType: '', personId: '', personName: '' })
                      }}
                      className={`new-account-payable-entity-button ${entityType === btn.id ? 'active' : ''}`}
                    >
                      <btn.icon size={14} />
                      {btn.label}
                    </button>
                  ))}
                </div>

                <div className="new-account-payable-form-grid">
                  <div className="new-account-payable-input-wrapper">
                    <label className="new-account-payable-input-label">
                      Nome do {entityType === 'supplier' ? 'Fornecedor' : entityType === 'customer' ? 'Cliente' : 'Funcionário'}
                    </label>
                    <div className="new-account-payable-search-input-wrapper">
                      <Search size={16} className="new-account-payable-search-icon" />
                      <input 
                        type="text" 
                        required
                        className="new-account-payable-search-input"
                        placeholder="Pesquisar registro..."
                        value={formData.personName}
                        onClick={() => setShowPersonModal(true)}
                        readOnly
                      />
                    </div>
                    {errors.personId && (
                      <span className="error-message">{errors.personId}</span>
                    )}
                  </div>

                  <div className="new-account-payable-input-wrapper">
                    <label className="new-account-payable-input-label">Descrição do Lançamento</label>
                    <input 
                      type="text" 
                      required
                      className="new-account-payable-input"
                      placeholder="Ex: Compra de luvas, Aluguel..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                    {errors.description && (
                      <span className="error-message">{errors.description}</span>
                    )}
                  </div>

                  <div className="new-account-payable-input-wrapper">
                    <label className="new-account-payable-input-label">Nº Faturamento / NF</label>
                    <div className="new-account-payable-search-input-wrapper">
                      <FileText size={16} className="new-account-payable-search-icon" />
                      <input 
                        type="text" 
                        className="new-account-payable-search-input"
                        placeholder="Opcional"
                        value={formData.invoiceNumber}
                        onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloco 2: Classificação e Temporalidade */}
            <div className="new-account-payable-cards-grid">
              <div className="new-account-payable-card">
                <h3 className="new-account-payable-card-title">
                  <Layers className="new-account-payable-card-title-icon" size={14} />
                  Classificação
                </h3>
                <div className="new-account-payable-card-content">
                  <CustomDropdown 
                    label="Categoria Principal"
                    options={categoryOptions}
                    value={formData.categoryId}
                    onChange={(val) => setFormData({...formData, categoryId: val, subcategoryId: ''})}
                    icon={Layers}
                    error={errors.categoryId}
                  />
                  
                  {formData.categoryId && subcategoryOptions.length > 1 && (
                    <CustomDropdown 
                      label="Subcategoria"
                      options={subcategoryOptions}
                      value={formData.subcategoryId}
                      onChange={(val) => setFormData({...formData, subcategoryId: val})}
                      icon={ListTree}
                      error={errors.subcategoryId}
                    />
                  )}
                  
                  <CustomDropdown 
                    label="Centro de Custo"
                    options={costCenterOptions}
                    value={formData.costCenterId}
                    onChange={(val) => setFormData({...formData, costCenterId: val})}
                    icon={Target}
                    error={errors.costCenterId}
                  />
                </div>
              </div>

              <div className="new-account-payable-card">
                <h3 className="new-account-payable-card-title">
                  <HelpCircle className="new-account-payable-card-title-icon" size={14} />
                  Temporalidade (DRE)
                </h3>
                <div className="new-account-payable-card-content">
                  <div className="new-account-payable-input-wrapper">
                    <label className="new-account-payable-input-label">Data de Competência</label>
                    <input 
                      type="date" 
                      className="new-account-payable-input"
                      value={formData.competenceDate}
                      onChange={(e) => setFormData({...formData, competenceDate: e.target.value})}
                    />
                  </div>
                  
                  <CustomDropdown 
                    label="Regra de Competência"
                    options={competencyRuleOptions}
                    value={formData.competenceDateType}
                    onChange={(val) => setFormData({...formData, competenceDateType: val})}
                    icon={HelpCircle}
                  />

                  <CustomDropdown 
                    label="Conta Bancária de Saída"
                    options={financialAccountOptions}
                    value={formData.financialAccountId}
                    onChange={(val) => setFormData({...formData, financialAccountId: val})}
                    icon={Wallet}
                    error={errors.financialAccountId}
                  />
                </div>
              </div>
            </div>

            {/* Bloco 3: Observações */}
            <div className="new-account-payable-card">
              <label className="new-account-payable-input-label">Observações Internas (Opcional)</label>
              <textarea 
                rows={4} 
                className="new-account-payable-textarea"
                placeholder="Detalhes adicionais relevantes para auditoria financeira..."
                value={formData.observations}
                onChange={(e) => setFormData({...formData, observations: e.target.value})}
              />
            </div>
          </div>

          {/* Coluna Lateral: Financeiro */}
          <div className="new-account-payable-sidebar">
            <div className="new-account-payable-sidebar-card">
              <h3 className="new-account-payable-sidebar-title">
                <Wallet className="new-account-payable-sidebar-title-icon" size={16} />
                Definições Financeiras
              </h3>

              <div className="new-account-payable-sidebar-content">
                <div className="new-account-payable-amount-wrapper">
                  <label className="new-account-payable-amount-label">Valor Total</label>
                  <div className="new-account-payable-amount-input-wrapper">
                    <span className="new-account-payable-amount-currency">R$</span>
                    <input 
                      type="number" 
                      step="0.01"
                      required
                      className="new-account-payable-amount-input"
                      placeholder="0,00"
                      value={formData.amount || ''}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    />
                  </div>
                  {errors.amount && (
                    <span className="error-message">{errors.amount}</span>
                  )}
                </div>

                <div className="new-account-payable-payment-type">
                  <label className="new-account-payable-payment-type-label">Tipo de Saída</label>
                  <div className="new-account-payable-payment-type-grid">
                    {[
                      { id: 'single', label: 'Única', icon: Receipt },
                      { id: 'installment', label: 'Parcelada', icon: ListOrdered },
                      { id: 'recurring_fixed', label: 'Fixo Recorr.', icon: Repeat },
                      { id: 'recurring_variable', label: 'Var. Recorr.', icon: TrendingUp }
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          installmentType: type.id,
                          numberOfInstallments: type.id === 'installment' ? 2 : 1
                        })}
                        className={`new-account-payable-payment-type-button ${formData.installmentType === type.id ? 'active' : ''}`}
                      >
                        <type.icon size={16} />
                        <span className="new-account-payable-payment-type-button-label">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {formData.installmentType === 'installment' && (
                  <div className="new-account-payable-installment-fields">
                    <div className="new-account-payable-installment-field">
                      <label className="new-account-payable-installment-label">Parcelas</label>
                      <input 
                        type="number" 
                        min="1"
                        className="new-account-payable-installment-input"
                        value={formData.numberOfInstallments}
                        onChange={(e) => setFormData({...formData, numberOfInstallments: parseInt(e.target.value) || 1})}
                      />
                    </div>
                    <div className="new-account-payable-installment-field">
                      <label className="new-account-payable-installment-label">1º Venc.</label>
                      <input 
                        type="date" 
                        className="new-account-payable-installment-input"
                        value={formData.firstDueDate}
                        onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                      />
                      {errors.firstDueDate && (
                        <span className="error-message">{errors.firstDueDate}</span>
                      )}
                    </div>
                  </div>
                )}

                {(formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && (
                  <div className="new-account-payable-recurring-fields">
                    <div className="new-account-payable-installment-fields">
                      <div className="new-account-payable-installment-field">
                        <label className="new-account-payable-installment-label">Período</label>
                        <select
                          className="new-account-payable-installment-input"
                          value={formData.period}
                          onChange={(e) => setFormData({...formData, period: e.target.value})}
                        >
                          {periodOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="new-account-payable-installment-field">
                        <label className="new-account-payable-installment-label">1º Venc.</label>
                        <input 
                          type="date" 
                          className="new-account-payable-installment-input"
                          value={formData.firstDueDate}
                          onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                        />
                        {errors.firstDueDate && (
                          <span className="error-message">{errors.firstDueDate}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {formData.installmentType === 'single' && (
                  <div className="new-account-payable-installment-fields">
                    <div className="new-account-payable-installment-field">
                      <label className="new-account-payable-installment-label">Vencimento</label>
                      <input 
                        type="date" 
                        className="new-account-payable-installment-input"
                        value={formData.firstDueDate}
                        onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                      />
                      {errors.firstDueDate && (
                        <span className="error-message">{errors.firstDueDate}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Lista de Parcelas */}
                {parcelas.length > 0 && (
                  <div className="new-account-payable-installments-list">
                    <p className="new-account-payable-installments-list-title">
                      Projeção da Grade
                    </p>
                    <div className="new-account-payable-installments-list-content">
                      {parcelas.map((parcela, idx) => (
                        <div key={idx} className="new-account-payable-installment-item">
                          <span className="new-account-payable-installment-item-number">
                            {parcela.number}/{parcelas.length}
                          </span>
                          <div className="new-account-payable-installment-item-content">
                            <input 
                              type="date" 
                              className="new-account-payable-installment-item-date"
                              value={parcela.dueDate}
                              onChange={(e) => updateParcelaDate(idx, e.target.value)}
                            />
                            <div className="new-account-payable-installment-item-amount-wrapper">
                              <span className="new-account-payable-installment-item-amount-currency">R$</span>
                              <input 
                                type="number"
                                step="0.01"
                                className="new-account-payable-installment-item-amount"
                                value={parcela.amount.toFixed(2)}
                                onChange={(e) => updateParcelaAmount(idx, e.target.value)}
                                disabled={formData.installmentType === 'recurring_fixed'}
                              />
                            </div>
                          </div>
                          <div className="new-account-payable-installment-item-badge">
                            <span className="new-account-payable-installment-item-badge-text">Provisão</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="new-account-payable-info-box">
                  <Info size={16} className="new-account-payable-info-icon" />
                  <p className="new-account-payable-info-text">
                    Os títulos serão baixados automaticamente após o <strong>vínculo com o extrato bancário</strong> na tela de conciliação.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}

      {errors.submit && (
        <div className="new-account-payable-error-message">{errors.submit}</div>
      )}

      <SelectPersonModal
        isOpen={showPersonModal}
        onClose={() => setShowPersonModal(false)}
        onSelect={handleSelectPerson}
        selectedType={entityType}
        selectedId={formData.personId}
      />
    </div>
  )
}

export default NovaContaPagarPage
